export type CourseTermOption = {
  label: string;
  value: string;
  season: "Spring" | "Fall";
  year: number;
};

type PersistedCourseTerm = {
  label?: string | null;
  season?: string | null;
  year?: number | null;
};

type AcademicTerm = {
  season: "Spring" | "Fall";
  year: number;
};

const normalizeSeason = (season: PersistedCourseTerm["season"]): CourseTermOption["season"] | null => {
  if (season === "Spring" || season === "SPRING") {
    return "Spring";
  }

  if (season === "Fall" || season === "FALL") {
    return "Fall";
  }

  return null;
};

const toCourseTermOption = ({ season, year }: AcademicTerm): CourseTermOption => {
  const label = `${season} ${year}`;
  return { label, value: label, season, year };
};

const compareTermOptions = (left: CourseTermOption, right: CourseTermOption): number => {
  if (left.year !== right.year) {
    return left.year - right.year;
  }

  const seasonOrder: Record<CourseTermOption["season"], number> = { Spring: 0, Fall: 1 };
  return seasonOrder[left.season] - seasonOrder[right.season];
};

const getCurrentSelectableTerm = (referenceDate: Date): AcademicTerm => {
  const month = referenceDate.getMonth();
  const year = referenceDate.getFullYear();

  if (month <= 4) {
    return { season: "Spring", year };
  }

  return { season: "Fall", year };
};

const getNextTerm = ({ season, year }: AcademicTerm): AcademicTerm => {
  if (season === "Spring") {
    return { season: "Fall", year };
  }

  return { season: "Spring", year: year + 1 };
};

export const getAcademicTermOptions = (referenceDate = new Date()): CourseTermOption[] => {
  const terms: AcademicTerm[] = [getCurrentSelectableTerm(referenceDate)];

  while (terms.length < 3) {
    const previousTerm = terms[terms.length - 1];
    terms.push(getNextTerm(previousTerm));
  }

  return terms.map(toCourseTermOption);
};

export const getCourseTermFilterOptions = (
  persistedTerms: PersistedCourseTerm[],
  referenceDate = new Date()
): CourseTermOption[] => {
  const optionsByValue = new Map<string, CourseTermOption>();

  persistedTerms.forEach((term) => {
    const season = normalizeSeason(term.season);
    if (!term.label || !season || typeof term.year !== "number") {
      return;
    }

    optionsByValue.set(term.label, { label: term.label, value: term.label, season, year: term.year });
  });

  getAcademicTermOptions(referenceDate).forEach((option) => optionsByValue.set(option.value, option));

  return Array.from(optionsByValue.values()).sort(compareTermOptions);
};
